import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

import { RootState, DocumentsState, Folder, File } from "@/types/store";
import {
  createDocument,
  getDocuments,
  getDocumentsByFolder,
  getDocumentsByFolderForUser,
  getDocumentsForUser,
  getMoreFiles,
  getMoreFilesByFolder,
  getMoreFilesByFolderForUser,
  getMoreFilesForUser,
  getMoreFolders,
  getMoreFoldersForUser,
  deleteFolder,
  deleteDocument,
  createFolder,
} from "@/api/profile/thunks";

const initialState: DocumentsState = {
  folders: [],
  foldersPage: 1,
  foldersEndReached: false,
  files: [],
  filesPage: 1,
  filesEndReached: false,
  filesLastFetchedForFolderId: "",
  filesInFolder: [],
  filesInFolderPage: 1,
  filesInFolderEndReached: false,
  loadingFolders: false,
  loadingFiles: false,
  loadingFilesInFolder: false,
  error: null,
  deletingFolder: false,
  deleteFolderError: null,
  deletingDocument: false,
  deleteDocumentError: null,
};

export const DocumentsSlice = createSlice({
  name: "DocumentsSlice",
  initialState,
  reducers: {
    setFoldersEndReached: (state, action) => {
      state.foldersEndReached = action.payload;
    },
    setFiles: (state, action) => {
      state.files = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDocuments.pending, (state) => {
        state.loadingFolders = true;
        state.loadingFiles = true;
        state.error = null;
      })
      .addCase(getDocuments.fulfilled, (state, { payload }) => {
        state.loadingFolders = false;
        state.loadingFiles = false;
        state.folders = payload.folders;
        state.files = payload.files;
        state.filesPage = 1;
        state.filesEndReached = !payload.hasNextPageFiles;
        state.foldersEndReached = !payload.hasNextPageFolders;
        state.foldersPage = 1;
      })
      .addCase(getDocuments.rejected, (state, action) => {
        state.loadingFolders = false;
        state.loadingFiles = false;
        state.error = action.payload as string;
      })

      .addCase(getDocumentsForUser.pending, (state) => {
        state.loadingFolders = true;
        state.loadingFiles = true;
        state.error = null;
      })
      .addCase(getDocumentsForUser.fulfilled, (state, { payload }) => {
        state.loadingFolders = false;
        state.loadingFiles = false;
        state.folders = payload.folders;
        state.files = payload.files;
        state.filesPage = 1;
        state.filesEndReached = !payload.hasNextPageFiles;
        state.foldersEndReached = !payload.hasNextPageFolders;
      })
      .addCase(getDocumentsForUser.rejected, (state, action) => {
        state.loadingFolders = false;
        state.loadingFiles = false;
        state.error = action.payload as string;
      })
      .addCase(getDocumentsByFolder.pending, (state) => {
        state.filesInFolderPage = 1;
        state.filesInFolderEndReached = false;
        state.filesInFolder = [];
        state.loadingFilesInFolder = true;
        state.error = null;
      })
      .addCase(getDocumentsByFolder.fulfilled, (state, { payload, meta }) => {
        state.loadingFilesInFolder = false;
        state.filesInFolder = payload.files;
        state.filesInFolderPage = 1;
        state.filesLastFetchedForFolderId = meta.arg.folderId;
        state.filesInFolderEndReached = !payload.hasNextPage;
      })
      .addCase(getDocumentsByFolder.rejected, (state, action) => {
        state.filesInFolderPage = 1;
        state.filesInFolderEndReached = true;
        state.filesInFolder = [];
        state.loadingFilesInFolder = false;
        state.error = action.payload as string;
      })
      .addCase(getDocumentsByFolderForUser.pending, (state) => {
        state.filesInFolderPage = 1;
        state.filesInFolderEndReached = false;
        state.filesInFolder = [];
        state.loadingFilesInFolder = true;
        state.error = null;
      })
      .addCase(
        getDocumentsByFolderForUser.fulfilled,
        (state, { payload, meta }) => {
          state.loadingFilesInFolder = false;
          state.filesInFolder = payload.files;
          state.filesInFolderPage = 1;
          state.filesLastFetchedForFolderId = meta.arg.folderId;
          state.filesInFolderEndReached = !payload.hasNextPage;
        }
      )
      .addCase(getDocumentsByFolderForUser.rejected, (state, action) => {
        state.loadingFilesInFolder = false;
        state.error = action.payload as string;
      })
      .addCase(getMoreFiles.fulfilled, (state, action) => {
        // Filter out documents that already exist in state to prevent duplicates
        const newFiles = action.payload.files.filter(
          (newFile: File) =>
            !state.files.some(
              (existingFile: File) => existingFile.id === newFile.id
            )
        );
        state.files = [...state.files, ...newFiles];
        state.filesPage += 1;
        state.filesEndReached = !action.payload.hasNextPageFiles;
      })
      .addCase(getMoreFiles.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getMoreFilesForUser.fulfilled, (state, action) => {
        // Filter out documents that already exist in state to prevent duplicates
        const newFiles = action.payload.files.filter(
          (newFile: { id: string }) =>
            !state.files.some((existingFile) => existingFile.id === newFile.id)
        );
        state.files = [...state.files, ...newFiles];
        state.filesPage += 1;
        state.filesEndReached = !action.payload.hasNextPageFiles;
      })
      .addCase(getMoreFilesForUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getMoreFilesByFolderForUser.fulfilled, (state, action) => {
        // Filter out documents that already exist in state to prevent duplicates
        const newFiles = action.payload.files.filter(
          (newFile: { id: string }) =>
            !state.filesInFolder.some(
              (existingFile) => existingFile.id === newFile.id
            )
        );
        state.filesInFolder = [...state.filesInFolder, ...newFiles];
        state.filesInFolderPage += 1;
        state.filesInFolderEndReached = !action.payload.hasNextPage;
      })
      .addCase(getMoreFilesByFolderForUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getMoreFilesByFolder.fulfilled, (state, action) => {
        // Filter out documents that already exist in state to prevent duplicates
        const newFiles = action.payload.files.filter(
          (newFile: { id: string }) =>
            !state.filesInFolder.some(
              (existingFile) => existingFile.id === newFile.id
            )
        );
        state.filesInFolder = [...state.filesInFolder, ...newFiles];
        state.filesInFolderPage += 1;
        state.filesInFolderEndReached = !action.payload.hasNextPage;
      })
      .addCase(getMoreFilesByFolder.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getMoreFolders.fulfilled, (state, action) => {
        // Ensure only unique folders are added by filtering out any folders that already exist in state
        const newFolders = action.payload.folders.filter(
          (newFolder: Folder) =>
            !state.folders.some(
              (existingFolder: Folder) => existingFolder.id === newFolder.id
            )
        );
        state.folders = [...state.folders, ...newFolders];
        state.foldersPage += 1;
      })
      .addCase(getMoreFolders.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getMoreFoldersForUser.fulfilled, (state, action) => {
        // Ensure only unique folders are added by filtering out any folders that already exist in state
        const newFolders = action.payload.folders.filter(
          (newFolder: Folder) =>
            !state.folders.some(
              (existingFolder: Folder) => existingFolder.id === newFolder.id
            )
        );
        state.folders = [...state.folders, ...newFolders];
        state.foldersPage += 1;
      })
      .addCase(getMoreFoldersForUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteFolder.pending, (state) => {
        state.deletingFolder = true;
        state.deleteFolderError = null;
        state.error = null;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.deletingFolder = false;
        state.folders = action.payload.folders;
        state.files = action.payload.files;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.deletingFolder = false;
        state.deleteFolderError = action.payload as string;
        state.error = action.payload as string;
      })
      .addCase(deleteDocument.pending, (state) => {
        state.deletingDocument = true;
        state.deleteDocumentError = null;
        state.error = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.deletingDocument = false;
        const deletedDocumentId = action.meta.arg.id;
        state.files = state.files.filter(
          (file) => file.id !== deletedDocumentId
        );
        state.filesInFolder = state.filesInFolder.filter(
          (file) => file.id !== deletedDocumentId
        );
        state.folders = state.folders.map((folder) => {
          if (folder.id === action.meta.arg.folderId) {
            folder.fileCount -= 1;
          }
          return folder;
        });
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.deletingDocument = false;
        state.deleteDocumentError = action.payload as string;
        state.error = action.payload as string;
      })
      .addCase(createDocument.fulfilled, (state, action) => {
        state.folders = state.folders.map((folder) => {
          if (folder.id === action.payload.folderId) {
            folder.fileCount += 1;
          }
          return folder;
        });
        state.files = [action.payload, ...state.files];
        if (state.filesLastFetchedForFolderId === action.payload.folderId) {
          state.filesInFolder = [action.payload, ...state.filesInFolder];
        }
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.folders = [action.payload.folders[0], ...state.folders];
      });
  },
});

export const { setFoldersEndReached, setFiles } = DocumentsSlice.actions;

export default DocumentsSlice.reducer;

export const useDocumentsData = () => {
  const folders = useSelector((state: RootState) => state.Documents.folders);
  const files = useSelector((state: RootState) => state.Documents.files);
  const loadingFolders = useSelector(
    (state: RootState) => state.Documents.loadingFolders
  );
  const loadingFiles = useSelector(
    (state: RootState) => state.Documents.loadingFiles
  );
  const error = useSelector((state: RootState) => state.Documents.error);
  const foldersPage = useSelector(
    (state: RootState) => state.Documents.foldersPage
  );
  const filesPage = useSelector(
    (state: RootState) => state.Documents.filesPage
  );
  const deletingFolder = useSelector(
    (state: RootState) => state.Documents.deletingFolder
  );
  const deleteFolderError = useSelector(
    (state: RootState) => state.Documents.deleteFolderError
  );
  const deletingDocument = useSelector(
    (state: RootState) => state.Documents.deletingDocument
  );
  const deleteDocumentError = useSelector(
    (state: RootState) => state.Documents.deleteFolderError
  );
  const foldersEndReached = useSelector(
    (state: RootState) => state.Documents.foldersEndReached
  );
  const filesEndReached = useSelector(
    (state: RootState) => state.Documents.filesEndReached
  );
  const filesInFolder = useSelector(
    (state: RootState) => state.Documents.filesInFolder
  );
  const filesInFolderPage = useSelector(
    (state: RootState) => state.Documents.filesInFolderPage
  );
  const filesInFolderEndReached = useSelector(
    (state: RootState) => state.Documents.filesInFolderEndReached
  );
  const loadingFilesInFolder = useSelector(
    (state: RootState) => state.Documents.loadingFilesInFolder
  );
  const filesLastFetchedForFolderId = useSelector(
    (state: RootState) => state.Documents.filesLastFetchedForFolderId
  );

  return {
    folders,
    files,
    loadingFolders,
    loadingFiles,
    error,
    foldersPage,
    filesPage,
    deletingFolder,
    deleteFolderError,
    deletingDocument,
    deleteDocumentError,
    foldersEndReached,
    filesEndReached,
    filesInFolder,
    filesInFolderPage,
    filesInFolderEndReached,
    loadingFilesInFolder,
    filesLastFetchedForFolderId,
  };
};
